const DB_NAME = "audio_upload_db"
const DB_VERSION = 1

interface SessionRecord {
    sessionId: string
    profileId: string
    creationDate: string
    totalChunks?: number
    copilotSessionId?: string
}

export async function requestPersistentStorage(): Promise<boolean> {
    if (typeof navigator === "undefined" || !navigator.storage?.persist) return false
    try {
        if (await navigator.storage.persisted?.()) return true
        return await navigator.storage.persist()
    } catch {
        return false
    }
}

interface ChunkRecord {
    id: string
    sessionId: string
    chunkIndex: number
    blob: Blob
    uploaded: boolean
}

let _dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
    if (_dbPromise) return _dbPromise
    _dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION)
        req.onupgradeneeded = (e) => {
            const db = (e.target as IDBOpenDBRequest).result
            if (!db.objectStoreNames.contains("sessions")) {
                db.createObjectStore("sessions", { keyPath: "sessionId" })
            }
            if (!db.objectStoreNames.contains("chunks")) {
                const store = db.createObjectStore("chunks", { keyPath: "id" })
                store.createIndex("by_session", "sessionId", { unique: false })
            }
        }
        req.onsuccess = () => {
            const db = req.result
            db.onversionchange = () => {
                db.close()
                _dbPromise = null
            }
            resolve(db)
        }
        req.onerror = () => {
            _dbPromise = null
            reject(req.error)
        }
    })
    return _dbPromise
}

function txGet<T>(db: IDBDatabase, storeName: string, key: IDBValidKey): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
        const t = db.transaction(storeName, "readonly")
        const req = t.objectStore(storeName).get(key)
        req.onsuccess = () => resolve(req.result as T | undefined)
        req.onerror = () => reject(req.error)
    })
}

function txPut(db: IDBDatabase, storeName: string, value: unknown): Promise<void> {
    return new Promise((resolve, reject) => {
        const t = db.transaction(storeName, "readwrite")
        const req = t.objectStore(storeName).put(value)
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
    })
}

function txGetAll<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
        const t = db.transaction(storeName, "readonly")
        const req = t.objectStore(storeName).getAll()
        req.onsuccess = () => resolve(req.result as T[])
        req.onerror = () => reject(req.error)
    })
}

export async function saveSession(session: SessionRecord): Promise<void> {
    const db = await openDb()
    await txPut(db, "sessions", session)
}

function txMerge(db: IDBDatabase, sessionId: string, patch: Partial<SessionRecord>): Promise<void> {
    return new Promise((resolve, reject) => {
        const t = db.transaction("sessions", "readwrite")
        const store = t.objectStore("sessions")
        const req = store.get(sessionId)
        req.onsuccess = () => {
            const session = req.result as SessionRecord | undefined
            if (session) store.put({ ...session, ...patch })
        }
        t.oncomplete = () => resolve()
        t.onerror = () => reject(t.error)
    })
}

export async function updateSessionFields(
    sessionId: string,
    patch: Partial<SessionRecord>,
): Promise<void> {
    const db = await openDb()
    await txMerge(db, sessionId, patch)
}

export async function updateSessionProfileId(sessionId: string, profileId: string): Promise<void> {
    return updateSessionFields(sessionId, { profileId })
}

export async function deleteSession(sessionId: string): Promise<void> {
    const db = await openDb()
    const chunks = await getSessionChunks(sessionId)
    return new Promise((resolve, reject) => {
        const t = db.transaction(["sessions", "chunks"], "readwrite")
        t.objectStore("sessions").delete(sessionId)
        for (const chunk of chunks) {
            t.objectStore("chunks").delete(chunk.id)
        }
        t.oncomplete = () => resolve()
        t.onerror = () => reject(t.error)
    })
}

export async function saveChunk(sessionId: string, chunkIndex: number, blob: Blob): Promise<void> {
    const db = await openDb()
    await txPut(db, "chunks", {
        id: `${sessionId}__${chunkIndex}`,
        sessionId,
        chunkIndex,
        blob,
        uploaded: false,
    })
}

export async function markChunkUploaded(sessionId: string, chunkIndex: number): Promise<void> {
    const db = await openDb()
    const id = `${sessionId}__${chunkIndex}`
    const chunk = await txGet<ChunkRecord>(db, "chunks", id)
    if (chunk) {
        await txPut(db, "chunks", { ...chunk, uploaded: true })
    }
}

export async function getSession(sessionId: string): Promise<SessionRecord | undefined> {
    const db = await openDb()
    return txGet<SessionRecord>(db, "sessions", sessionId)
}

export async function getActiveSessions(): Promise<SessionRecord[]> {
    const db = await openDb()
    return txGetAll<SessionRecord>(db, "sessions")
}

export async function getSessionChunks(sessionId: string): Promise<ChunkRecord[]> {
    const db = await openDb()
    return new Promise((resolve, reject) => {
        const t = db.transaction("chunks", "readonly")
        const req = t.objectStore("chunks").index("by_session").getAll(sessionId)
        req.onsuccess = () => resolve((req.result as ChunkRecord[]).sort((a, b) => a.chunkIndex - b.chunkIndex))
        req.onerror = () => reject(req.error)
    })
}
